import moment, { Moment } from "moment";
import { Bond } from "../../pages/debt-service-page.tsx";
import { PrimaryPricingUtils } from "./primary-pricing.utils.ts";

export type BuilderOutputRow = {
  date: string,
  curve: number,
  spread: number,
  bond: Bond,
  coupon: number,
}

export type BuilderOutput = BuilderOutputRow[]

export type ProductionRow = {
  maturity: string,
  curve: number,
  spread: number,
  ytm: number,
  ytw: number,
  price: number,
  production: number,
  bond: Bond,
  locked: boolean,
}

export type ProductionTable = ProductionRow[];

export class ProductionUtils {

  /**
   * Calculates the production information.
   */
  public static calculateProduction(
    bonds: Bond[],
    currentTable: ProductionTable,
    builderOutput: BuilderOutput, 
    deliveryDate: Moment, 
    callDate: Moment
  ): ProductionTable {
    if (bonds.length === 0) return [];
    
    const result: ProductionTable = [];

    for (const bond of bonds) {
      const output = builderOutput.find(x => x.bond === bond);
      const current = currentTable.find(x => x.bond === bond);
      if (!output && !current) continue;

      const locked = current !== undefined && current.locked;
      const spread = locked || !output ? current!.spread : output.spread;
      const curve = !output ? current!.curve : output.curve;

      const yieldToCall = curve + spread;
      const maturityAfterCallDate = moment(bond.maturity).isAfter(callDate, "day");
      const rowHasYTM = maturityAfterCallDate && yieldToCall < bond.coupon;

      const price = PrimaryPricingUtils.calculateBondPrice(
        rowHasYTM ? callDate : moment(bond.maturity),
        moment(deliveryDate),
        bond.coupon,
        yieldToCall
      );

      const ytm = PrimaryPricingUtils.calculateYieldToMaturity(
        moment(bond.maturity),
        moment(deliveryDate),
        bond.coupon,
        price
      );

      const ytw = Math.min(yieldToCall, ytm);
      const amount = this.getBondPrincipal(bond);
      const production = amount * (price / 100);
      const callSpread = yieldToCall - curve;
      result.push({ curve, spread: callSpread, maturity: bond.maturity, price, ytm, ytw, production, bond, locked });
    }

    return result;
  }

  public static getBondPrincipal(bond: Bond): number {
    return bond.type === "serial" ? 
      bond.principal : 
      bond.redemptions.reduce((prev, curr) => prev + curr.amount, 0)
  }

}
